/**
 * PlansPage.tsx — Enterprise plan catalog management
 *
 * Full CRUD interface for billing plans. Click any row to open a detail
 * slide-in panel from the right. Drill deeper into features and role mapping.
 *
 * @module packages/billing-admin/src/pages/PlansPage
 * @since 2026-06-04
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import DataTable, { type Column } from '../components/DataTable';
import ConfirmDialog from '../components/ConfirmDialog';
import BulkActionBar from '../components/BulkActionBar';
import PlanEditForm from '../components/plan/PlanEditForm';
import SlidePanel, { usePanelNav } from '../components/SlidePanel';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { useOrgContext } from '../contexts/OrgContext';
import { useAdminPlansList, useArchivePlan, useDeletePlan, usePlanPricingModels, useAddPricingModel, useUpdatePricingModel, useDeletePricingModel } from '../hooks/useAdminApi50';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { Plan, PlanPricingModel } from '../hooks/useAdminApi50';
import PricingModelConfigForm, { MODEL_TYPE_LABELS, MODEL_TYPE_COLORS, MODEL_TYPE_BADGE_COLORS, formatMoney, formatModelSummary } from '../components/plan/PricingModelConfigForm';

// ---------------------------------------------------------------------------
// Entitlement types + hooks
// ---------------------------------------------------------------------------

interface EntitlementDef {
  id: string;
  slug: string;
  displayName: string;
  description: string | null;
  planTier: string | null;
  entitlementType: string;
  authBusinessRoleId: string | null;
  isActive: boolean;
  displayOrder: number;
}

function useEntitlementCatalog() {
  const { token, apiBaseUrl } = useAdminAuth();
  const { activeOrg } = useOrgContext();
  return useQuery({
    queryKey: ['entitlements', 'catalog', activeOrg?.orgId],
    queryFn: async () => {
      const res = await fetch(`${apiBaseUrl}/api/v1/orgs/${activeOrg!.orgId}/entitlements`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`${res.status}`);
      return (await res.json() as { entitlements: EntitlementDef[] }).entitlements;
    },
    enabled: !!token && !!activeOrg?.orgId,
  });
}

function usePlanEntitlements(planId: string) {
  const { token, apiBaseUrl } = useAdminAuth();
  const { activeOrg } = useOrgContext();
  return useQuery({
    queryKey: ['entitlements', 'plan', planId, activeOrg?.orgId],
    queryFn: async () => {
      const res = await fetch(`${apiBaseUrl}/api/v1/orgs/${activeOrg!.orgId}/entitlements/plan/${planId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`${res.status}`);
      return (await res.json() as { entitlementIds: string[] }).entitlementIds;
    },
    enabled: !!token && !!activeOrg?.orgId,
  });
}

function useSavePlanEntitlements() {
  const { token, apiBaseUrl } = useAdminAuth();
  const { activeOrg } = useOrgContext();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ planId, entitlementIds }: { planId: string; entitlementIds: string[] }) => {
      const res = await fetch(`${apiBaseUrl}/api/v1/orgs/${activeOrg!.orgId}/entitlements/plan/${planId}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ entitlementIds }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
    },
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: ['entitlements', 'plan', vars.planId] });
    },
  });
}

function useSyncEntitlements() {
  const { token, apiBaseUrl } = useAdminAuth();
  const { activeOrg } = useOrgContext();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await fetch(`${apiBaseUrl}/api/v1/orgs/${activeOrg!.orgId}/entitlements/sync`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`${res.status}`);
      return res.json() as Promise<{ created: number; updated: number }>;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['entitlements', 'catalog'] });
    },
  });
}

function StatusBadge({ status }: { status: string }): React.JSX.Element {
  const styles = status === 'active'
    ? 'bg-emerald-100 text-emerald-800'
    : 'bg-slate-100 text-slate-600';
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${styles}`}>
      {status}
    </span>
  );
}

function PricingModelBadge({ model }: { model: string }): React.JSX.Element {
  const colors: Record<string, string> = {
    flat: 'bg-blue-100 text-blue-800',
    per_seat: 'bg-violet-100 text-violet-800',
    tiered: 'bg-amber-100 text-amber-800',
    usage: 'bg-cyan-100 text-cyan-800',
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${colors[model] ?? 'bg-slate-100 text-slate-700'}`}>
      {model.replace('_', ' ')}
    </span>
  );
}


function PricingModelsSection({ planId }: { planId: string }): React.JSX.Element {
  const { data: models, isLoading } = usePlanPricingModels(planId);
  const addModel = useAddPricingModel();
  const updateModel = useUpdatePricingModel();
  const deleteModel = useDeletePricingModel();
  const [addOpen, setAddOpen] = useState(false);
  const [addType, setAddType] = useState<string>('flat');
  const [addConfig, setAddConfig] = useState<Record<string, unknown>>({});
  const [editModel, setEditModel] = useState<PlanPricingModel | null>(null);
  const [editConfig, setEditConfig] = useState<Record<string, unknown>>({});

  function openEdit(model: PlanPricingModel): void {
    setEditModel(model);
    setEditConfig(model.config as Record<string, unknown>);
  }

  async function handleAdd(): Promise<void> {
    await addModel.mutateAsync({ planId, model_type: addType, config: addConfig, display_order: (models ?? []).length });
    setAddOpen(false);
    setAddConfig({});
  }

  async function handleSaveEdit(): Promise<void> {
    if (!editModel) return;
    await updateModel.mutateAsync({ planId, modelId: editModel.id, updates: { config: editConfig } });
    setEditModel(null);
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] uppercase tracking-wider text-slate-500">Pricing Models</p>
        <button onClick={() => { setAddOpen(true); setAddType('flat'); setAddConfig({}); }} className="text-xs font-medium text-cyan-700 hover:text-cyan-800">+ Add Model</button>
      </div>

      {isLoading && <p className="text-xs text-slate-400">Loading…</p>}

      {!isLoading && (models ?? []).length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-300 p-6 text-center">
          <p className="text-sm text-slate-500">No pricing models configured</p>
          <p className="text-xs text-slate-400 mt-1">Add models to define how this plan bills customers.</p>
        </div>
      )}

      <div className="space-y-2">
        {(models ?? []).map((model) => (
          <div
            key={model.id}
            onClick={() => openEdit(model)}
            className={`group relative flex items-center gap-3 rounded-xl border p-4 cursor-pointer transition hover:shadow-sm ${MODEL_TYPE_COLORS[model.model_type] ?? 'border-slate-200 bg-slate-50'}`}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold">{MODEL_TYPE_LABELS[model.model_type] ?? model.model_type}</p>
                {!model.is_active && <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] text-slate-600">paused</span>}
              </div>
              <p className="text-xs mt-0.5 opacity-80">{formatModelSummary(model.model_type, model.config as Record<string, unknown>)}</p>
              {model.model_type === 'usage' && (model.config as Record<string, string>).pricing_catalog_id && (
                <p className="text-[10px] font-mono opacity-50 mt-0.5">catalog: {String((model.config as Record<string, string>).pricing_catalog_id).slice(0, 8)}…</p>
              )}
            </div>
            <svg className="h-4 w-4 text-slate-400 opacity-0 group-hover:opacity-100 transition" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" /></svg>
            <button
              onClick={(e) => { e.stopPropagation(); void deleteModel.mutateAsync({ planId, modelId: model.id }); }}
              className="rounded-md bg-white/70 px-2 py-1 text-[10px] font-medium text-red-600 opacity-0 transition group-hover:opacity-100"
            >✕</button>
          </div>
        ))}
      </div>

      {/* Add pricing model modal */}
      {addOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-slate-900">Add Pricing Model</h3>
            <p className="mt-1 text-sm text-slate-500">Stack multiple pricing models on this plan. Each applies independently.</p>
            <div className="mt-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700">Model Type</label>
                <select value={addType} onChange={(e) => { setAddType(e.target.value); setAddConfig({}); }} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm">
                  <option value="flat">Flat Rate — fixed monthly/annual fee</option>
                  <option value="per_seat">Per Seat — per user/seat charge</option>
                  <option value="usage">Usage Based — metered by event consumption</option>
                  <option value="tiered">Tiered — volume-based pricing brackets</option>
                </select>
              </div>
              <PricingModelConfigForm modelType={addType} config={addConfig} setConfig={setAddConfig} />
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setAddOpen(false)} className="rounded-md border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">Cancel</button>
              <button onClick={() => void handleAdd()} disabled={addModel.isPending} className="rounded-md bg-cyan-600 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-700 disabled:opacity-50">{addModel.isPending ? 'Adding…' : 'Add Model'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit pricing model modal */}
      {editModel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-slate-900">Edit {MODEL_TYPE_LABELS[editModel.model_type] ?? editModel.model_type}</h3>
            <p className="mt-1 text-sm text-slate-500">Update the configuration for this pricing model.</p>
            <div className="mt-5">
              <PricingModelConfigForm modelType={editModel.model_type} config={editConfig} setConfig={setEditConfig} />
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setEditModel(null)} className="rounded-md border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">Cancel</button>
              <button onClick={() => void handleSaveEdit()} disabled={updateModel.isPending} className="rounded-md bg-cyan-600 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-700 disabled:opacity-50">{updateModel.isPending ? 'Saving…' : 'Save Changes'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Plan Detail Content (rendered inside the SlidePanel)
// ---------------------------------------------------------------------------

function PlanDetailContent({ plan, onEdit }: { plan: Plan; onEdit: () => void }): React.JSX.Element {
  const { push } = usePanelNav();
  const { apiBaseUrl, token } = useAdminAuth();
  const { activeOrg } = useOrgContext();
  const orgBase = `${apiBaseUrl}/api/v1/orgs/${activeOrg?.orgId ?? 'default'}`;
  const authHeaders = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Fetch automation rules for this plan
  const { data: planRuleIds = [] } = useQuery<string[]>({
    queryKey: ['plan-automation-rules', plan.id],
    queryFn: async () => {
      const res = await fetch(`${orgBase}/automation-rules/by-plan/${plan.id}`, { headers: authHeaders });
      if (!res.ok) return [];
      const data = await res.json();
      return data.ruleIds;
    },
  });
  const { data: allRules = [] } = useQuery<Array<{ id: string; name: string; triggerEvent: string; actionType: string; isActive: boolean }>>({
    queryKey: ['automation-rules'],
    queryFn: async () => {
      const res = await fetch(`${orgBase}/automation-rules`, { headers: authHeaders });
      if (!res.ok) return [];
      const data = await res.json();
      return data.rules;
    },
  });

  return (
    <div className="px-6 py-6 space-y-6">
      {/* Status + interval + ID */}
      <div className="flex items-center gap-3">
        <StatusBadge status={plan.status} />
        <span className="text-sm text-slate-500">{plan.interval} billing</span>
        {plan.seed_key && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-mono text-slate-500">{plan.seed_key}</span>}
      </div>

      {/* Multi-Model Pricing — THE primary section */}
      <PricingModelsSection planId={plan.id} />
      {/* Plan ID */}
      <div className="rounded-xl border border-slate-100 bg-white p-4">
        <p className="text-[10px] uppercase tracking-wider text-slate-500">Plan ID</p>
        <p className="mt-1 font-mono text-xs text-slate-600">{plan.id}</p>
      </div>

      {/* Tiers */}
      {plan.tiers && plan.tiers.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Tier Breakpoints</p>
          <div className="rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-slate-600">From</th>
                  <th className="px-4 py-2 text-left font-medium text-slate-600">To</th>
                  <th className="px-4 py-2 text-right font-medium text-slate-600">Unit Price</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {plan.tiers.map((tier, i) => (
                  <tr key={i}>
                    <td className="px-4 py-2 text-slate-700">{tier.min_qty}</td>
                    <td className="px-4 py-2 text-slate-700">{tier.max_qty ?? '∞'}</td>
                    <td className="px-4 py-2 text-right font-medium text-slate-900">
                      {formatMoney(tier.unit_price, plan.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Marketing Features — display items for pricing pages */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] uppercase tracking-wider text-slate-500">Marketing Features</p>
          <Link to="/marketing-features" className="text-xs font-medium text-cyan-700 hover:text-cyan-800">
            Manage →
          </Link>
        </div>
        {plan.features && plan.features.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {plan.features.map((feature) => (
              <span
                key={feature}
                className="rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 text-xs text-emerald-800"
              >
                {feature}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-400">No marketing features assigned</p>
        )}
      </div>

      {/* Entitlements — permissions / access control */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] uppercase tracking-wider text-slate-500">Entitlements (Permissions)</p>
          <button
            onClick={() => push({
              id: 'entitlements',
              title: 'Entitlement Mapping',
              subtitle: plan.name,
              category: 'Access Control',
              content: <PlanFeaturesScreen plan={plan} />,
            })}
            className="text-xs font-medium text-cyan-700 hover:text-cyan-800"
          >
            Configure →
          </button>
        </div>
        <p className="text-xs text-slate-400">Configure which permissions/roles are granted when a customer subscribes to this plan.</p>
      </div>

      {/* Automation Rules for this plan */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] uppercase tracking-wider text-slate-500">Automation Rules</p>
          <button
            onClick={() => push({
              id: 'plan-automation',
              title: 'Automation Rules',
              subtitle: plan.name,
              category: 'Operations',
              content: <PlanAutomationRulesScreen plan={plan} allRules={allRules} currentRuleIds={planRuleIds} orgBase={orgBase} authHeaders={authHeaders} />,
            })}
            className="text-xs font-medium text-cyan-700 hover:text-cyan-800"
          >
            Configure →
          </button>
        </div>
        {planRuleIds.length > 0 ? (
          <div className="space-y-1.5">
            {planRuleIds.map(rid => {
              const rule = allRules.find(r => r.id === rid);
              return rule ? (
                <div key={rid} className="flex items-center gap-2 rounded-lg border border-slate-100 bg-white px-3 py-2 text-sm">
                  <span className={`w-2 h-2 rounded-full ${rule.isActive ? 'bg-green-400' : 'bg-slate-300'}`} />
                  <span className="text-slate-700">{rule.name}</span>
                </div>
              ) : null;
            })}
          </div>
        ) : (
          <p className="text-sm text-slate-400">No automation rules attached</p>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-2">
        <button
          onClick={onEdit}
          className="flex-1 rounded-xl bg-cyan-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-cyan-800 transition"
        >
          Edit Plan
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Automation Rules Screen (pushed inside the panel)
// ---------------------------------------------------------------------------

interface PlanAutomationRulesScreenProps {
  plan: Plan;
  allRules: Array<{ id: string; name: string; triggerEvent: string; actionType: string; isActive: boolean }>;
  currentRuleIds: string[];
  orgBase: string;
  authHeaders: Record<string, string>;
}

function PlanAutomationRulesScreen({ plan, allRules, currentRuleIds, orgBase, authHeaders }: PlanAutomationRulesScreenProps): React.JSX.Element {
  const qc = useQueryClient();
  const [rules, setRules] = useState(allRules);
  const [selected, setSelected] = useState<Set<string>>(new Set(currentRuleIds));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Inline create state
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newTrigger, setNewTrigger] = useState('');
  const [newAction, setNewAction] = useState('');
  const [newDelay, setNewDelay] = useState(0);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  useEffect(() => {
    setSelected(new Set(currentRuleIds));
  }, [currentRuleIds]);

  useEffect(() => {
    setRules(allRules);
  }, [allRules]);

  function toggle(ruleId: string) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(ruleId)) next.delete(ruleId); else next.add(ruleId);
      return next;
    });
    setDirty(true);
    setSaved(false);
  }

  async function handleSave() {
    setSaving(true);
    try {
      await fetch(`${orgBase}/automation-rules/by-plan/${plan.id}`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify({ ruleIds: [...selected] }),
      });
      qc.invalidateQueries({ queryKey: ['plan-automation-rules', plan.id] });
      qc.invalidateQueries({ queryKey: ['automation-rules-plan-mappings'] });
      setDirty(false);
      setSaved(true);
    } finally {
      setSaving(false);
    }
  }

  async function handleCreateRule() {
    if (!newName || !newTrigger || !newAction) {
      setCreateError('Name, trigger, and action are required');
      return;
    }
    setCreating(true);
    setCreateError('');
    try {
      const res = await fetch(`${orgBase}/automation-rules`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          name: newName,
          triggerEvent: newTrigger,
          actionType: newAction,
          delayDays: newDelay,
          isActive: true,
        }),
      });
      if (!res.ok) throw new Error('Failed to create rule');
      const { rule } = await res.json();

      // Auto-assign to this plan
      setRules(prev => [...prev, rule]);
      setSelected(prev => new Set([...prev, rule.id]));
      setDirty(true);

      // Also save the plan association immediately
      const currentIds = [...selected, rule.id];
      await fetch(`${orgBase}/automation-rules/by-plan/${plan.id}`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify({ ruleIds: currentIds }),
      });

      qc.invalidateQueries({ queryKey: ['automation-rules'] });
      qc.invalidateQueries({ queryKey: ['plan-automation-rules', plan.id] });
      qc.invalidateQueries({ queryKey: ['automation-rules-plan-mappings'] });

      // Reset form
      setShowCreate(false);
      setNewName('');
      setNewTrigger('');
      setNewAction('');
      setNewDelay(0);
      setDirty(false);
      setSaved(true);
    } catch (e: any) {
      setCreateError(e.message);
    } finally {
      setCreating(false);
    }
  }

  function applyTemplate(tpl: { name: string; trigger: string; action: string; delay: number }) {
    setNewName(tpl.name);
    setNewTrigger(tpl.trigger);
    setNewAction(tpl.action);
    setNewDelay(tpl.delay);
    setShowCreate(true);
  }

  const TRIGGERS = [
    { value: 'subscription.created', label: 'Subscription Created', icon: '🟢' },
    { value: 'subscription.canceled', label: 'Subscription Cancelled', icon: '🔴' },
    { value: 'subscription.expired', label: 'Subscription Expired', icon: '⚫' },
    { value: 'invoice.past_due', label: 'Invoice Past Due', icon: '⚠️' },
    { value: 'payment.failed', label: 'Payment Failed', icon: '❌' },
    { value: 'payment.succeeded', label: 'Payment Succeeded', icon: '✅' },
    { value: 'customer.reinstated', label: 'Customer Reinstated', icon: '🔄' },
    { value: 'customer.suspended', label: 'Customer Suspended', icon: '🚫' },
  ];
  const ACTIONS = [
    { value: 'send_webhook', label: 'Send Webhook', icon: '🔗' },
    { value: 'send_email', label: 'Send Email', icon: '📧' },
    { value: 'grant_entitlements', label: 'Grant Entitlements', icon: '🔓' },
    { value: 'revoke_entitlements', label: 'Revoke Entitlements', icon: '🔒' },
    { value: 'restrict_access', label: 'Restrict Access', icon: '⚡' },
    { value: 'notify_external', label: 'Notify External', icon: '📡' },
  ];
  const TEMPLATES = [
    { name: 'Provision on Subscribe', trigger: 'subscription.created', action: 'grant_entitlements', delay: 0 },
    { name: 'Deprovision on Cancel', trigger: 'subscription.canceled', action: 'revoke_entitlements', delay: 0 },
    { name: 'Payment Reminder (7d)', trigger: 'invoice.past_due', action: 'send_email', delay: 7 },
    { name: 'Suspend Access (14d)', trigger: 'invoice.past_due', action: 'revoke_entitlements', delay: 14 },
    { name: 'Restore on Reinstate', trigger: 'customer.reinstated', action: 'grant_entitlements', delay: 0 },
  ];

  const triggerIcon = (v: string) => TRIGGERS.find(t => t.value === v)?.icon ?? '⚡';
  const triggerLabel = (v: string) => TRIGGERS.find(t => t.value === v)?.label ?? v;
  const actionIcon = (v: string) => ACTIONS.find(a => a.value === v)?.icon ?? '⚙️';
  const actionLabel = (v: string) => ACTIONS.find(a => a.value === v)?.label ?? v;

  return (
    <div className="px-6 py-6 space-y-4">
      <p className="text-sm text-slate-500">
        Manage automation rules for the <strong>{plan.name}</strong> plan.
      </p>

      {/* Existing rules list */}
      {rules.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Attach existing rules</p>
          {rules.map(rule => (
            <button
              key={rule.id}
              onClick={() => toggle(rule.id)}
              className={`w-full flex items-center gap-3 rounded-lg border px-4 py-3 text-left transition-all ${
                selected.has(rule.id)
                  ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500'
                  : 'border-slate-200 bg-white hover:border-slate-300'
              }`}
            >
              <input
                type="checkbox"
                checked={selected.has(rule.id)}
                readOnly
                className="rounded border-slate-300 text-indigo-600 pointer-events-none"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm text-slate-800">{rule.name}</span>
                  {!rule.isActive && (
                    <span className="text-[10px] rounded-full bg-slate-100 text-slate-500 px-1.5 py-0.5">paused</span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-0.5 text-xs text-slate-500">
                  <span>{triggerIcon(rule.triggerEvent)} {triggerLabel(rule.triggerEvent)}</span>
                  <span className="text-slate-300">→</span>
                  <span>{actionIcon(rule.actionType)} {actionLabel(rule.actionType)}</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Save existing associations */}
      {rules.length > 0 && (
        <div className="flex items-center gap-3">
          <button
            onClick={() => void handleSave()}
            disabled={!dirty || saving}
            className="rounded-lg bg-indigo-600 px-5 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          {saved && <span className="text-sm text-green-600">✓ Saved</span>}
          {dirty && !saved && <span className="text-xs text-amber-600">Unsaved changes</span>}
        </div>
      )}

      {/* Divider */}
      <div className="border-t border-slate-200 pt-4">
        <div className="flex items-center justify-between">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Create new rule</p>
          {!showCreate && (
            <button
              onClick={() => setShowCreate(true)}
              className="text-xs font-medium text-indigo-600 hover:text-indigo-700"
            >
              + Custom Rule
            </button>
          )}
        </div>
      </div>

      {/* Quick templates */}
      {!showCreate && (
        <div className="grid grid-cols-1 gap-2">
          {TEMPLATES.map((tpl, i) => (
            <button
              key={i}
              onClick={() => applyTemplate(tpl)}
              className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 text-left hover:border-indigo-300 hover:bg-indigo-50/30 transition-all group"
            >
              <div className="flex items-center gap-1.5 text-sm shrink-0">
                <span>{triggerIcon(tpl.trigger)}</span>
                <span className="text-slate-300">→</span>
                {tpl.delay > 0 && (
                  <>
                    <span className="text-xs text-amber-600 font-medium">{tpl.delay}d</span>
                    <span className="text-slate-300">→</span>
                  </>
                )}
                <span>{actionIcon(tpl.action)}</span>
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium text-slate-700 group-hover:text-indigo-700">{tpl.name}</span>
              </div>
              <span className="text-xs text-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity">Create →</span>
            </button>
          ))}
        </div>
      )}

      {/* Inline create form */}
      {showCreate && (
        <div className="rounded-lg border border-indigo-200 bg-indigo-50/30 p-4 space-y-3">
          {createError && (
            <div className="rounded bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">{createError}</div>
          )}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Rule Name</label>
            <input
              type="text"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              placeholder="e.g., Provision on Subscribe"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Trigger</label>
            <select
              value={newTrigger}
              onChange={e => setNewTrigger(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
            >
              <option value="">Select trigger...</option>
              {TRIGGERS.map(t => (
                <option key={t.value} value={t.value}>{t.icon} {t.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Action</label>
            <select
              value={newAction}
              onChange={e => setNewAction(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
            >
              <option value="">Select action...</option>
              {ACTIONS.map(a => (
                <option key={a.value} value={a.value}>{a.icon} {a.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Delay (days)</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={0}
                value={newDelay}
                onChange={e => setNewDelay(parseInt(e.target.value) || 0)}
                className="w-20 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
              />
              <span className="text-xs text-slate-500">
                {newDelay === 0 ? 'Immediate' : `${newDelay} day${newDelay > 1 ? 's' : ''} delay`}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 pt-1">
            <button
              onClick={() => void handleCreateRule()}
              disabled={creating || !newName || !newTrigger || !newAction}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {creating ? 'Creating…' : 'Create & Attach'}
            </button>
            <button
              onClick={() => { setShowCreate(false); setCreateError(''); }}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Features Screen (pushed inside the panel)
// ---------------------------------------------------------------------------

function PlanFeaturesScreen({ plan }: { plan: Plan }): React.JSX.Element {
  const { data: catalog, isLoading: catalogLoading } = useEntitlementCatalog();
  const { data: mappedIds, isLoading: mappingLoading } = usePlanEntitlements(plan.id);
  const saveMutation = useSavePlanEntitlements();
  const syncMutation = useSyncEntitlements();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(false);

  // Initialize selected from server data
  useEffect(() => {
    if (mappedIds) {
      setSelected(new Set(mappedIds));
      setDirty(false);
    }
  }, [mappedIds]);

  function toggle(id: string): void {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setDirty(true);
    setSaved(false);
  }

  async function handleSave(): Promise<void> {
    await saveMutation.mutateAsync({ planId: plan.id, entitlementIds: [...selected] });
    setDirty(false);
    setSaved(true);
  }

  const isLoading = catalogLoading || mappingLoading;

  // Group entitlements by planTier
  const grouped = (catalog ?? []).reduce<Record<string, EntitlementDef[]>>((acc, e) => {
    const tier = e.planTier ?? 'Other';
    (acc[tier] ??= []).push(e);
    return acc;
  }, {});

  return (
    <div className="px-6 py-6 space-y-5">
      {/* Entitlement Mapping */}
      <div className="rounded-xl bg-cyan-50 border border-cyan-200 p-4">
        <p className="text-sm font-medium text-cyan-900">Entitlement Mapping for: {plan.name}</p>
        <p className="mt-1 text-xs text-cyan-700">
          Select which entitlements (mapped from auth business roles) are granted when a user subscribes to this plan.
          Changes are saved to the database and drive automatic provisioning/deprovisioning.
        </p>
      </div>

      {isLoading && <p className="text-sm text-slate-400">Loading entitlements...</p>}

      {!isLoading && (catalog ?? []).length === 0 && (
        <div className="rounded-xl border border-dashed border-amber-300 bg-amber-50 p-6 text-center space-y-3">
          <p className="text-sm text-amber-800 font-medium">No entitlements found</p>
          <p className="text-xs text-amber-600">
            Sync business roles from your auth system to populate the entitlement catalog.
          </p>
          <button
            onClick={() => void syncMutation.mutateAsync()}
            disabled={syncMutation.isPending}
            className="rounded-lg bg-amber-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
          >
            {syncMutation.isPending ? 'Syncing...' : 'Sync from Auth'}
          </button>
        </div>
      )}

      {!isLoading && Object.entries(grouped).map(([tier, entitlements]) => (
        <div key={tier}>
          <div className="flex items-center gap-2 mb-2">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{tier} Tier</p>
            <span className="text-[10px] text-slate-400">{entitlements.length} entitlements</span>
          </div>
          <div className="space-y-1.5">
            {entitlements.map((ent) => (
              <label
                key={ent.id}
                className={`flex items-center gap-3 rounded-xl border p-3 cursor-pointer transition ${
                  selected.has(ent.id)
                    ? 'border-cyan-300 bg-cyan-50/50'
                    : 'border-slate-200 bg-white hover:border-slate-300'
                }`}
              >
                <input
                  type="checkbox"
                  checked={selected.has(ent.id)}
                  onChange={() => toggle(ent.id)}
                  className="h-4 w-4 rounded border-slate-300 text-cyan-600"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-slate-900">{ent.displayName}</p>
                    <span className="font-mono text-[10px] text-slate-400">{ent.slug}</span>
                  </div>
                  {ent.description && (
                    <p className="text-[11px] text-slate-500 mt-0.5">{ent.description}</p>
                  )}
                </div>
              </label>
            ))}
          </div>
        </div>
      ))}

      <div className="flex items-center gap-3">
        <button
          onClick={() => void handleSave()}
          disabled={!dirty || saveMutation.isPending}
          className="flex-1 rounded-xl bg-cyan-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-cyan-800 transition disabled:opacity-50"
        >
          {saveMutation.isPending ? 'Saving...' : saved ? '✓ Saved' : 'Save Entitlement Mapping'}
        </button>
        <button
          onClick={() => void syncMutation.mutateAsync()}
          disabled={syncMutation.isPending}
          className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
        >
          {syncMutation.isPending ? 'Syncing...' : 'Sync from Auth'}
        </button>
      </div>
    </div>
  );
}

export default function PlansPage(): React.JSX.Element {
  const { data: plans, isLoading, error, refetch } = useAdminPlansList();
  const archivePlan = useArchivePlan();
  const deletePlan = useDeletePlan();

  const [editPlan, setEditPlan] = useState<Plan | null | undefined>(undefined);
  const [archiveTarget, setArchiveTarget] = useState<Plan | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Plan | null>(null);
  const [detailPlan, setDetailPlan] = useState<Plan | null>(null);
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'archived'>('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const filteredPlans = (plans ?? []).filter((plan) => {
    if (filterStatus === 'all') return true;
    return plan.status === filterStatus;
  });

  const activeCount = (plans ?? []).filter((p) => p.status === 'active').length;
  const archivedCount = (plans ?? []).filter((p) => p.status === 'archived').length;

  const someSelected = selected.size > 0;

  async function handleArchive(): Promise<void> {
    if (!archiveTarget) return;
    await archivePlan.mutateAsync(archiveTarget.id);
    setArchiveTarget(null);
    void refetch();
  }

  async function handleDelete(): Promise<void> {
    if (!deleteTarget) return;
    await deletePlan.mutateAsync(deleteTarget.id);
    setDeleteTarget(null);
    void refetch();
  }

  async function handleBulkDelete(): Promise<void> {
    setBulkDeleting(true);
    try {
      await Promise.all([...selected].map((id) => deletePlan.mutateAsync(id)));
      setSelected(new Set());
      setBulkDeleteOpen(false);
      void refetch();
    } finally {
      setBulkDeleting(false);
    }
  }

  const columns: Column<Plan>[] = [
    {
      key: 'name',
      header: 'Plan',
      render: (row) => (
        <div>
          <button
            onClick={() => setDetailPlan(row)}
            className="text-left font-medium text-slate-900 hover:text-cyan-700 hover:underline"
          >
            {row.name}
          </button>
          {row.seed_key && (
            <p className="mt-0.5 text-[11px] font-mono text-slate-400">{row.seed_key}</p>
          )}
        </div>
      ),
    },
    {
      key: 'pricing_model',
      header: 'Models',
      render: (row) => (
        <div className="flex flex-wrap gap-1">
          {(row.pricing_models && row.pricing_models.length > 0)
            ? row.pricing_models.map((m) => <PricingModelBadge key={m.id} model={m.model_type} />)
            : <PricingModelBadge model={row.pricing_model} />
          }
        </div>
      ),
    },
    {
      key: 'price',
      header: 'Price',
      render: (row) => {
        // Show summary from pricing models if available
        if (row.pricing_models && row.pricing_models.length > 0) {
          const flat = row.pricing_models.find((m) => m.model_type === 'flat');
          if (flat) {
            const c = flat.config as Record<string, unknown>;
            return (
              <div>
                <p className="font-medium text-slate-900">{formatMoney(Number(c.amount_cents ?? 0) / 100, String(c.currency ?? 'USD'))}</p>
                <p className="text-xs text-slate-500">/{row.interval}{row.pricing_models.length > 1 ? ` +${row.pricing_models.length - 1} more` : ''}</p>
              </div>
            );
          }
          return <p className="text-xs text-slate-500">{row.pricing_models.length} model{row.pricing_models.length !== 1 ? 's' : ''}</p>;
        }
        return (
          <div>
            <p className="font-medium text-slate-900">{formatMoney(row.price, row.currency)}</p>
            <p className="text-xs text-slate-500">/{row.interval}</p>
          </div>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: 'actions',
      header: '',
      sortable: false,
      render: (row) => (
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => setEditPlan(row)}
            className="rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-cyan-700 hover:bg-cyan-50"
          >
            Edit
          </button>
          {row.status === 'active' && (
            <button
              onClick={() => setArchiveTarget(row)}
              className="rounded-md border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
            >
              Archive
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <AdminLayout title="Plan Catalog">
      <div className="space-y-6">
        {/* Header */}
        <section className="rounded-[28px] border border-white/70 bg-white/85 p-6 shadow-[0_30px_90px_-50px_rgba(15,23,42,0.7)] backdrop-blur">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-cyan-700">Product catalog</p>
              <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-950">Plan Management</h1>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                Create and manage billing plans. Configure pricing models, set intervals, define tier breakpoints, and control which plans are available for new subscriptions.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-2">
                  <p className="text-[11px] uppercase tracking-wider text-slate-500">Total</p>
                  <p className="text-xl font-semibold text-slate-900">{(plans ?? []).length}</p>
                </div>
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2">
                  <p className="text-[11px] uppercase tracking-wider text-emerald-600">Active</p>
                  <p className="text-xl font-semibold text-emerald-700">{activeCount}</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-2">
                  <p className="text-[11px] uppercase tracking-wider text-slate-500">Archived</p>
                  <p className="text-xl font-semibold text-slate-500">{archivedCount}</p>
                </div>
              </div>
              <button
                onClick={() => setEditPlan(null)}
                className="rounded-full bg-cyan-400 px-5 py-3 text-sm font-semibold text-slate-950 transition hover:bg-cyan-300"
              >
                Create Plan
              </button>
            </div>
          </div>
        </section>

        {/* Filters */}
        <div className="flex gap-2">
          {(['all', 'active', 'archived'] as const).map((f) => (
            <button
              key={f}
              onClick={() => {
                setFilterStatus(f);
                setSelected(new Set());
              }}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition ${
                filterStatus === f
                  ? 'bg-slate-900 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {f === 'all' ? 'All Plans' : f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        {error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {error.message}
          </div>
        )}

        {someSelected && (
          <BulkActionBar
            count={selected.size}
            label="plan"
            onDelete={() => setBulkDeleteOpen(true)}
            onClear={() => setSelected(new Set())}
            deleting={bulkDeleting}
          />
        )}

        <DataTable<Plan>
          columns={columns}
          rows={filteredPlans}
          rowKey={(row) => row.id}
          loading={isLoading}
          emptyMessage="No plans found. Create your first plan to get started."
          selectable
          selectedKeys={selected}
          onSelectionChange={setSelected}
          onRowClick={(row) => setDetailPlan(row)}
        />
      </div>

      {/* Create / Edit slide panel */}
      <SlidePanel
        open={editPlan !== undefined}
        onClose={() => setEditPlan(undefined)}
        size="lg"
        title={editPlan === null ? 'Create Plan' : `Edit: ${editPlan?.name ?? ''}`}
        category="Plan Configuration"
      >
        <div className="px-6 py-6">
          <PlanEditForm
            plan={editPlan ?? null}
            onSuccess={() => {
              setEditPlan(undefined);
              void refetch();
            }}
            onCancel={() => setEditPlan(undefined)}
          />
        </div>
      </SlidePanel>

      {/* Detail slide panel */}
      <SlidePanel
        open={detailPlan !== null}
        onClose={() => setDetailPlan(null)}
        size="md"
        title={detailPlan?.name}
        subtitle={detailPlan ? `${detailPlan.interval} · ${detailPlan.pricing_models?.length ?? 0} pricing model${(detailPlan.pricing_models?.length ?? 0) !== 1 ? 's' : ''}` : undefined}
        category="Plan Details"
      >
        {detailPlan && (
          <PlanDetailContent
            plan={detailPlan}
            onEdit={() => {
              const p = detailPlan;
              setDetailPlan(null);
              setEditPlan(p);
            }}
          />
        )}
      </SlidePanel>

      {/* Archive confirm */}
      <ConfirmDialog
        open={archiveTarget !== null}
        title="Archive Plan"
        message={`Archive "${archiveTarget?.name}"? Archived plans cannot be assigned to new subscriptions. Existing subscriptions are unaffected.`}
        confirmLabel="Archive"
        cancelLabel="Cancel"
        onConfirm={() => void handleArchive()}
        onCancel={() => setArchiveTarget(null)}
        loading={archivePlan.isPending}
      />

      {/* Delete confirm */}
      <ConfirmDialog
        open={deleteTarget !== null}
        title="Permanently Delete Plan"
        message={
          <div>
            <p>Permanently delete <strong>"{deleteTarget?.name}"</strong>?</p>
            <p className="mt-2 text-red-600 font-medium">This action cannot be undone. The plan and all associated pricing data will be removed.</p>
          </div>
        }
        confirmLabel="Delete Forever"
        cancelLabel="Cancel"
        onConfirm={() => void handleDelete()}
        onCancel={() => setDeleteTarget(null)}
        loading={deletePlan.isPending}
      />

      {/* Bulk delete confirm */}
      <ConfirmDialog
        open={bulkDeleteOpen}
        title={`Delete ${selected.size} Plan${selected.size !== 1 ? 's' : ''}`}
        message={
          <div>
            <p>Permanently delete <strong>{selected.size} plan{selected.size !== 1 ? 's' : ''}</strong>?</p>
            <p className="mt-2 text-red-600 font-medium">This cannot be undone. All selected plans and their pricing data will be removed.</p>
          </div>
        }
        confirmLabel={`Delete ${selected.size} Plan${selected.size !== 1 ? 's' : ''}`}
        cancelLabel="Cancel"
        onConfirm={() => void handleBulkDelete()}
        onCancel={() => setBulkDeleteOpen(false)}
        loading={bulkDeleting}
      />
    </AdminLayout>
  );
}
